import { env } from '@sib/config';
import { LOGIN_BASE_REQUEST } from './constants';
import { cacheExtensionTokens } from './extension-token-cache';
import { msalInstance, setRedirectInterceptor } from './msal-instance';
import { useAuthStore } from './auth.store';

const redirectUri = env.isExtension
  ? `https://${chrome?.runtime?.id}.chromiumapp.org`
  : '';

let tokenUrl: string | null = null;

/**
 * Generates a silent token renewal URL via acquireTokenRedirect.
 * Falls back to loginRedirect with prompt: 'none' when MSAL cache is
 * unavailable (e.g. encryption key lost after extension close/reopen).
 */
const acquireTokenUrl = async () => {
  await msalInstance.handleRedirectPromise();

  return new Promise<string>((resolve, reject) => {
    if (tokenUrl) {
      resolve(tokenUrl);

      return;
    }

    setRedirectInterceptor(url => {
      tokenUrl = url;
      resolve(url);
    });

    const loginHint = useAuthStore.getState().account?.username;

    msalInstance
      .acquireTokenRedirect({
        ...LOGIN_BASE_REQUEST,
        redirectUri,
        prompt: 'none',
        ...(loginHint && { loginHint }),
      })
      .catch(() => {
        msalInstance
          .loginRedirect({
            ...LOGIN_BASE_REQUEST,
            redirectUri,
            prompt: 'none',
            ...(loginHint && { loginHint }),
          })
          .catch(error => {
            setRedirectInterceptor(null);
            reject(error);
          });
      });
  });
};

/**
 * Generates a login url
 */
const launchWebAuthFlow = async (url: string, interactive = true) => {
  const responseUrl = await chrome.identity.launchWebAuthFlow({
    interactive,
    url,
  });

  if (responseUrl?.includes('#')) {
    const data = await msalInstance.handleRedirectPromise({
      hash: `#${responseUrl.split('#')[1]}`,
    });

    return data;
  }

  return null;
};

let pendingFallback: ReturnType<typeof executeTokenFallback> | null = null;

const executeTokenFallback = async () => {
  try {
    const url = await acquireTokenUrl();
    tokenUrl = null;

    const result = await launchWebAuthFlow(url);

    if (result?.account) msalInstance.setActiveAccount(result.account);

    return result;
  } catch {
    return null;
  } finally {
    pendingFallback = null;
  }
};

export const getExtensionTokenFallback = () => {
  if (!env.isExtension) return Promise.resolve(null);

  if (pendingFallback) return pendingFallback;

  pendingFallback = executeTokenFallback();

  return pendingFallback;
};

let loginUrl: string | null = null;

/**
 * Generates a login url
 */
export const getLoginUrl = async () =>
  new Promise<string>((resolve, reject) => {
    if (loginUrl) {
      resolve(loginUrl);

      return;
    }

    setRedirectInterceptor(url => {
      loginUrl = url;
      resolve(url);
    });

    msalInstance
      .loginRedirect({
        ...LOGIN_BASE_REQUEST,
        redirectUri,
        prompt: 'select_account',
      })
      .catch(error => {
        setRedirectInterceptor(null);
        reject(error);
      });
  });

export const loginAction = async () => {
  const loginUrl = await getLoginUrl();

  const result = await launchWebAuthFlow(loginUrl);

  if (!result) throw new Error('Login failed');

  msalInstance.setActiveAccount(result.account);

  cacheExtensionTokens({
    idToken: result.idToken,
    accessToken: result.accessToken,
    expiresOn: result.expiresOn,
  });

  return result;
};
